import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  CheckCircle2,
  DollarSign,
  FileCheck2,
  Globe2,
  MapPinned,
  Plane,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';

interface AdminRHAssurancesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const summaryCards = [
  {
    label: 'Position actuelle',
    value: 'Services majoritairement à distance',
    detail: 'Les services hors Canada sont rendus principalement depuis le Canada.',
    icon: Globe2,
    tone: 'sky',
  },
  {
    label: 'Présence hors Canada',
    value: 'Aucune présence permanente',
    detail: 'Aucun bureau, employé, établissement ou inventaire hors Canada à documenter pour le moment.',
    icon: Building2,
    tone: 'emerald',
  },
  {
    label: 'Point de vigilance',
    value: 'Territoire mondial',
    detail: "Un avenant ou une validation territoriale est à conserver pour la facturation en Australie.",
    icon: ShieldCheck,
    tone: 'amber',
  },
];

const territories = [
  {
    country: 'Canada',
    detail: 'Territoire principal des opérations et point de livraison normal des services.',
  },
  {
    country: 'Australie',
    detail: 'Facturation directe à une maison mère australienne depuis le 1er juillet 2026.',
  },
  {
    country: 'États-Unis',
    detail: 'Services indirects via DataDis et déplacements physiques occasionnels, normalement maximum 1 par année.',
  },
];

const services = [
  'Infonuagique',
  'Service TI, support et conseil client',
  'Cybersécurité',
  'Téléphonie',
  'Licences',
  'Installation, dépannage ou formation lors de visites validées',
];

const revenueRows = [
  {
    source: 'Australie',
    current: '3 500 $ CAD / mois',
    forecast2026: '21 000 $ CAD',
    annualized: '42 000 $ CAD',
    note: 'Client facturé directement en Australie; succursale desservie au Québec.',
  },
  {
    source: 'DataDis / États-Unis indirect',
    current: '700 $ CAD / mois',
    forecast2026: '10 000 $ CAD',
    annualized: '24 000 $ CAD',
    note: 'Estimation basée sur une montée prévue vers 2 000 $ CAD / mois.',
  },
];

const coverageTypes = [
  'Responsabilité civile générale',
  'Responsabilité professionnelle / erreurs et omissions, si applicable',
  'Cyberrisque, si lié à la même discussion ou police',
  'Toute autre couverture corporative pertinente à relier plus tard',
];

const generalDocuments = [
  'Police d’assurance actuelle',
  'Preuve d’assurance',
  'Courriels ou confirmations du courtier / assureur',
  'Historique des renouvellements et déclarations annuelles',
];

const outsideCanadaDocuments = [
  'Avenant territoire mondial, si émis',
  'Confirmation du courtier / assureur pour la facturation en Australie',
  'Notes de calcul des revenus hors Canada',
  'Historique des validations liées aux nouveaux pays ou déplacements',
];

const generalToDocument = [
  'Assureur principal',
  'Courtier / contact assurance',
  'Numéros de police',
  'Périodes de couverture et dates de renouvellement',
  'Limites, franchises et primes',
  'Exclusions importantes',
  'Preuves d’assurance disponibles',
  'Responsable interne et processus de révision',
];

const governanceItems = [
  {
    title: 'Responsable interne',
    detail: 'Administration / direction, avec Mélissa comme point naturel de suivi si confirmé.',
  },
  {
    title: 'Révision minimale',
    detail: 'À chaque renouvellement d’assurance ou dès qu’un nouveau pays, revenu important ou déplacement récurrent apparaît.',
  },
  {
    title: 'Déclencheurs',
    detail: 'Nouveau territoire, hausse significative des revenus hors Canada, plus d’un voyage USA ou nouveau type de service.',
  },
];

const toneClasses = {
  sky: 'border-sky-300/30 bg-sky-500/10 text-sky-100',
  emerald: 'border-emerald-300/30 bg-emerald-500/10 text-emerald-100',
  amber: 'border-amber-300/30 bg-amber-500/10 text-amber-100',
};

export default function AdminRHAssurancesModal({ isOpen, onClose }: AdminRHAssurancesModalProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'outsideCanada'>('general');

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 28, scale: 0.97 }}
            transition={{ type: 'spring', damping: 24, stiffness: 220 }}
            className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-teal-300/30 bg-slate-950 text-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 border-b border-white/10 bg-slate-950/95 px-8 py-6 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-teal-300/40 bg-teal-400/10 px-4 py-2 text-sm font-semibold text-teal-100">
                    <ShieldCheck className="h-4 w-4" />
                    Administration & RH · Conformité · Assurances
                  </div>
                  <h2 className="text-3xl font-black tracking-tight md:text-4xl">
                    Assurances corporatives & responsabilité
                  </h2>
                  <p className="mt-3 max-w-4xl text-base leading-relaxed text-slate-300">
                    Source de vérité interne pour documenter les assurances corporatives BZ. L’onglet général servira
                    de dossier maître; l’onglet Services hors Canada centralise les informations déjà déclarées ou à
                    valider avec l’assureur.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-full border border-white/10 bg-white/10 p-3 text-white transition hover:bg-white/20"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="space-y-8 px-8 py-8">
              <div className="flex flex-wrap gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-2">
                <button
                  type="button"
                  onClick={() => setActiveTab('general')}
                  className={`rounded-xl px-5 py-3 text-sm font-bold transition ${
                    activeTab === 'general'
                      ? 'bg-teal-400 text-slate-950 shadow-lg shadow-teal-900/30'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  Général
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('outsideCanada')}
                  className={`rounded-xl px-5 py-3 text-sm font-bold transition ${
                    activeTab === 'outsideCanada'
                      ? 'bg-sky-400 text-slate-950 shadow-lg shadow-sky-900/30'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  Services hors Canada
                </button>
              </div>

              {activeTab === 'general' && (
                <>
                  <section className="rounded-3xl border border-amber-300/30 bg-amber-500/10 p-6">
                    <div className="mb-5 flex items-center gap-3">
                      <AlertTriangle className="h-7 w-7 text-amber-200" />
                      <h3 className="text-2xl font-bold">À documenter</h3>
                    </div>
                    <p className="max-w-4xl text-sm leading-relaxed text-amber-50/90">
                      Cette section est volontairement gardée comme dossier maître à compléter. Elle regroupera les
                      informations générales sur les assurances corporatives BZ, séparément du dossier spécifique aux
                      services hors Canada.
                    </p>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {generalToDocument.map((item) => (
                        <div key={item} className="rounded-2xl border border-amber-200/20 bg-slate-950/40 p-4 text-sm text-amber-50">
                          {item}
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-3xl border border-violet-300/30 bg-violet-500/10 p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <FileCheck2 className="h-7 w-7 text-violet-200" />
                        <h3 className="text-2xl font-bold">Couvertures à relier</h3>
                      </div>
                      <ul className="space-y-3 text-sm leading-relaxed text-violet-50/90">
                        {coverageTypes.map((coverage) => (
                          <li key={coverage} className="flex gap-3">
                            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-violet-200" />
                            <span>{coverage}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="rounded-3xl border border-slate-600 bg-slate-900/80 p-6">
                      <div className="mb-5 flex items-center gap-3">
                        <FileCheck2 className="h-7 w-7 text-slate-200" />
                        <h3 className="text-2xl font-bold">Documents généraux à conserver</h3>
                      </div>
                      <div className="grid gap-3">
                        {generalDocuments.map((document) => (
                          <div key={document} className="flex gap-3 rounded-2xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-200">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                            <span>{document}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  <section className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
                    <div className="rounded-3xl border border-cyan-300/30 bg-cyan-500/10 p-6">
                      <div className="mb-5 flex items-center gap-3">
                        <CalendarClock className="h-7 w-7 text-cyan-200" />
                        <h3 className="text-2xl font-bold">Gouvernance et suivi général</h3>
                      </div>
                      <div className="grid gap-4 md:grid-cols-3">
                        {governanceItems.map((item) => (
                          <div key={item.title} className="rounded-2xl border border-cyan-200/20 bg-slate-950/40 p-4">
                            <h4 className="font-bold text-cyan-100">{item.title}</h4>
                            <p className="mt-2 text-xs leading-relaxed text-cyan-50/80">{item.detail}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <Users className="h-7 w-7 text-teal-200" />
                        <h3 className="text-2xl font-bold">Parties prenantes</h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <img src="/MP.png" alt="Mélissa" className="h-12 w-12 rounded-full border-2 border-teal-200 object-cover" title="Mélissa - Administration" />
                        <img src="/LAH.jpg" alt="LAH" className="h-12 w-12 rounded-full border-2 border-teal-200 object-cover" title="LAH - Opérations admin" />
                        <img src="/FP.png" alt="François Delisle" className="h-12 w-12 rounded-full border-2 border-teal-200 object-cover" title="François Delisle - Gouvernance corporative" />
                        <img src="/DB.png" alt="Dany" className="h-12 w-12 rounded-full border-2 border-teal-200 object-cover" title="Dany - Direction / amélioration continue" />
                      </div>
                    </div>
                  </section>
                </>
              )}

              {activeTab === 'outsideCanada' && (
                <>
                  <section className="grid gap-5 md:grid-cols-3">
                    {summaryCards.map(({ label, value, detail, icon: Icon, tone }) => (
                      <div key={label} className={`rounded-2xl border p-5 ${toneClasses[tone as keyof typeof toneClasses]}`}>
                        <Icon className="mb-3 h-8 w-8" />
                        <p className="text-sm uppercase opacity-75">{label}</p>
                        <h3 className="mt-2 text-xl font-bold">{value}</h3>
                        <p className="mt-2 text-sm leading-relaxed opacity-90">{detail}</p>
                      </div>
                    ))}
                  </section>

                  <section className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
                    <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                      <div className="mb-5 flex items-center gap-3">
                        <Globe2 className="h-7 w-7 text-teal-200" />
                        <h3 className="text-2xl font-bold">Portrait des services hors Canada</h3>
                      </div>
                      <div className="space-y-4">
                        {territories.map((territory) => (
                          <div key={territory.country} className="rounded-2xl border border-teal-300/20 bg-teal-400/5 p-5">
                            <h4 className="text-lg font-bold text-teal-100">{territory.country}</h4>
                            <p className="mt-2 text-sm leading-relaxed text-slate-300">{territory.detail}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-amber-300/30 bg-amber-500/10 p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <AlertTriangle className="h-7 w-7 text-amber-200" />
                        <h3 className="text-2xl font-bold">Déplacements physiques</h3>
                      </div>
                      <div className="space-y-4 text-sm leading-relaxed text-amber-50/90">
                        <p>
                          Jusqu’à une visite par année aux États-Unis pourrait être requise pour une installation, du
                          dépannage client ou de la formation.
                        </p>
                        <p>
                          Si plus d’un déplacement devait être requis, la situation doit être validée au préalable et
                          documentée dans PRIsME.
                        </p>
                        <div className="rounded-2xl border border-amber-300/30 bg-slate-950/40 p-4">
                          <div className="flex items-center gap-2 font-bold text-amber-100">
                            <Plane className="h-4 w-4" />
                            Lien opérationnel
                          </div>
                          <p className="mt-2 text-xs text-amber-50/80">
                            Référer aussi à la tuile « Politique déplacements USA — douanes & immigration » pour le volet
                            voyage, mandat, preuves et validation pré-départ.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="rounded-3xl border border-sky-300/30 bg-sky-500/10 p-6">
                    <div className="mb-5 flex items-center gap-3">
                      <DollarSign className="h-7 w-7 text-sky-200" />
                      <h3 className="text-2xl font-bold">Revenus déclarés ou estimés hors Canada</h3>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-sky-200/20">
                      <table className="w-full border-collapse text-left text-sm">
                        <thead className="bg-sky-950/70 text-sky-100">
                          <tr>
                            <th className="px-4 py-3 font-bold">Source</th>
                            <th className="px-4 py-3 font-bold">Actuel</th>
                            <th className="px-4 py-3 font-bold">2026</th>
                            <th className="px-4 py-3 font-bold">Annuel</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-sky-200/10">
                          {revenueRows.map((row) => (
                            <tr key={row.source} className="bg-slate-900/50 align-top">
                              <td className="px-4 py-4">
                                <div className="font-bold text-white">{row.source}</div>
                                <div className="mt-1 text-xs leading-relaxed text-slate-400">{row.note}</div>
                              </td>
                              <td className="px-4 py-4 text-slate-200">{row.current}</td>
                              <td className="px-4 py-4 text-slate-200">{row.forecast2026}</td>
                              <td className="px-4 py-4 text-slate-200">{row.annualized}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-sky-100/80">
                      Tous les montants sont en dollars canadiens (CAD). Les montants doivent être révisés lors du
                      renouvellement ou si le volume augmente de façon importante.
                    </p>
                  </section>

                  <section className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-3xl border border-emerald-300/30 bg-emerald-500/10 p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <CheckCircle2 className="h-7 w-7 text-emerald-200" />
                        <h3 className="text-2xl font-bold">Services à déclarer clairement</h3>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {services.map((service) => (
                          <div key={service} className="rounded-xl border border-emerald-200/20 bg-slate-950/30 p-3 text-sm text-emerald-50">
                            {service}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-600 bg-slate-900/80 p-6">
                      <div className="mb-5 flex items-center gap-3">
                        <FileCheck2 className="h-7 w-7 text-slate-200" />
                        <h3 className="text-2xl font-bold">Documents spécifiques à conserver</h3>
                      </div>
                      <div className="grid gap-3">
                        {outsideCanadaDocuments.map((document) => (
                          <div key={document} className="flex gap-3 rounded-2xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-200">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                            <span>{document}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  <section className="rounded-3xl border border-rose-300/30 bg-rose-500/10 p-6">
                    <div className="mb-4 flex items-center gap-3">
                      <MapPinned className="h-7 w-7 text-rose-200" />
                      <h3 className="text-2xl font-bold">Limites importantes</h3>
                    </div>
                    <ul className="space-y-3 text-sm leading-relaxed text-rose-50/90">
                      <li>Services hors Canada principalement rendus à distance depuis le Canada.</li>
                      <li>Aucune présence opérationnelle permanente hors Canada, sauf indication contraire documentée.</li>
                      <li>Aucun inventaire, bureau ou employé hors Canada à ce jour.</li>
                      <li>Tout changement important doit être validé avant d’être présenté comme couvert.</li>
                    </ul>
                  </section>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
